/* eslint-disable react/display-name */
import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { Box, Container, Typography, Paper, TextField, InputAdornment, IconButton, Menu, MenuItem, ListItemIcon, Button, Dialog, DialogTitle, DialogContent, CircularProgress } from '@mui/material'
import { gql, useQuery, useMutation } from '@apollo/client'
import ZoneComponent from '../components/Zone/Zone'
import CustomLoader from '../components/Loader/CustomLoader'
import { getZones, deleteZone } from '../apollo'
import DataTable from 'react-data-table-component'
import orderBy from 'lodash/orderBy'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import MapIcon from '@mui/icons-material/Map'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CloseIcon from '@mui/icons-material/Close'

const GET_ZONES = gql`${getZones}`
const DELETE_ZONE = gql`${deleteZone}`

const Zones = props => {
  const [editModal, setEditModal] = useState(false)
  const [addModal, setAddModal] = useState(false)
  const [selectedZone, setSelectedZone] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  const [mutate] = useMutation(DELETE_ZONE, { refetchQueries: [{ query: GET_ZONES }] })
  const { data, loading: loadingQuery, error } = useQuery(GET_ZONES)

  useEffect(() => { localStorage.removeItem('restaurant_id') }, [])

  const allZones = data?.zones ?? []
  const regex = searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null
  const filtered = searchQuery.length < 3 ? allZones : allZones.filter(z =>
    z.title?.toLowerCase().search(regex) > -1 || z.description?.toLowerCase().search(regex) > -1
  )

  const ActionMenu = ({ row }) => {
    const [anchor, setAnchor] = useState(null)
    return (
      <>
        <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#f1f5f9' } }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
          PaperProps={{ elevation: 0, sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f1f5f9', minWidth: 160 } }}>
          <MenuItem onClick={() => { setAnchor(null); setSelectedZone(row); setEditModal(true) }} sx={{ borderRadius: '8px', mx: 0.5, my: 0.25, fontSize: '0.83rem' }}>
            <ListItemIcon><EditIcon fontSize="small" sx={{ color: '#3b82f6' }} /></ListItemIcon>Edit
          </MenuItem>
          <MenuItem onClick={() => { setAnchor(null); mutate({ variables: { id: row._id } }) }} sx={{ borderRadius: '8px', mx: 0.5, my: 0.25, fontSize: '0.83rem', color: '#ef4444' }}>
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>Delete
          </MenuItem>
        </Menu>
      </>
    )
  }

  const columns = [
    {
      name: 'ZONE', sortable: true, selector: row => row.title, minWidth: '180px',
      cell: row => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <LocationOnIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>{row.title}</Typography>
        </Box>
      )
    },
    {
      name: 'DESCRIPTION', hide: 'sm', sortable: true, selector: row => row.description, minWidth: '260px',
      cell: row => <Typography sx={{ fontSize: '0.83rem', color: '#64748b' }}>{row.description || '-'}</Typography>
    },
    { name: '', right: true, width: '60px', cell: row => <ActionMenu row={row} /> }
  ]

  return (
    <>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MapIcon sx={{ color: '#93c5fd', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em' }}>Zones</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Define delivery zones and assign riders</Typography>
            </Box>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedZone(null); setAddModal(true) }}
            sx={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', fontWeight: 700, px: 3, py: 1.2, borderRadius: '12px', boxShadow: '0 4px 16px rgba(59,130,246,0.45)', textTransform: 'none', '&:hover': { background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', transform: 'translateY(-1px)' }, transition: 'all 0.2s ease' }}>
            Add Zone
          </Button>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <Paper elevation={0} sx={{ background: 'linear-gradient(135deg,#eff6ff,#dbeafe)', border: '1px solid #bfdbfe', borderRadius: '16px', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', minWidth: 160 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: '#3b82f618', border: '1px solid #3b82f630', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6', flexShrink: 0 }}><MapIcon sx={{ fontSize: 22 }} /></Box>
            <Box>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{allZones.length}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500, mt: 0.2 }}>Total Zones</Typography>
            </Box>
          </Paper>
        </Box>

        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
              All Zones <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>({filtered.length})</Typography>
            </Typography>
            <TextField size="small" placeholder="Search zones..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>, sx: { borderRadius: '10px', fontSize: '0.83rem', bgcolor: '#fff' } }}
              sx={{ width: 260 }} />
          </Box>
          {error && <Box sx={{ px: 3, py: 2, bgcolor: '#fef2f2' }}><Typography sx={{ fontSize: '0.83rem', color: '#dc2626' }}>Error: {error.message}</Typography></Box>}
          {loadingQuery ? <CustomLoader /> : (
            <DataTable columns={columns} data={filtered} pagination noHeader
              sortFunction={(rows, field, dir) => orderBy(rows, row => row[field]?.toString().toLowerCase(), dir)}
              customStyles={{ headRow: { style: { backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', minHeight: '44px' } }, headCells: { style: { fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' } }, rows: { style: { borderBottom: '1px solid #f1f5f9', minHeight: '60px', '&:hover': { backgroundColor: '#f8fafc' } } }, cells: { style: { paddingLeft: '16px', paddingRight: '16px' } } }}
              noDataComponent={<Box py={8} textAlign="center"><MapIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} /><Typography sx={{ color: '#64748b' }}>No zones found</Typography></Box>}
            />
          )}
        </Paper>
      </Container>

      {/* Add Modal */}
      <Dialog open={addModal} onClose={() => setAddModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px', overflow: 'visible' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">Add New Zone</Typography>
          <IconButton size="small" onClick={() => setAddModal(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3, overflow: 'visible' }}>
          <ZoneComponent onClose={() => setAddModal(false)} />
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModal} onClose={() => setEditModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px', overflow: 'visible' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">Edit Zone</Typography>
          <IconButton size="small" onClick={() => setEditModal(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3, overflow: 'visible' }}>
          <ZoneComponent zone={selectedZone} onClose={() => setEditModal(false)} />
        </DialogContent>
      </Dialog>
    </>
  )
}

export default withTranslation()(Zones)